/**
 * customsirge_product_sticky_atc.js
 * Sticky ATC bar — shows after the buy box scrolls out of view (Figma 27063:1527 annotation).
 *
 * The sticky variant controls are PROXIES to the hero buy box (#product-variants inside
 * form#add-to-cart), which is the single source of truth. Rather than cloning the whole
 * #product-variants (which contains a hidden raw/OptionSelectors set as well as the real one),
 * we rebuild the sticky from ONLY the real pill groups (.radio-selector) + the real size
 * <select>, deduped per option — so the hidden duplicate set can never leak in.
 *
 * How "the real one" is identified depends on the mode:
 *   - normally      -> by visibility (the duplicate set is display:none)
 *   - bundle mode   -> by class, because Kaching bundles mode (hide_quantity_picker) hides the WHOLE
 *                      hero picker block, which zeroes the geometry of every descendant. Selecting by
 *                      visibility there would find nothing and the sticky bar would render empty.
 *                      The real controls are the wrappers WITHOUT `single-option-selector`.
 *
 * Sticky pill click -> .click() the matching hero radio; sticky size change -> set hero size
 * select value + dispatch change. A delegated change listener on the hero reflects authoritative
 * state back into the sticky (pill checked state + styling, size value, price). Reflect never
 * dispatches, so there are no feedback loops. Add-to-Cart proxies a .click() to the hero button.
 */
(function () {
  function init() {
    var bar = document.querySelector('[data-customsirge-sticky-atc]');
    if (!bar) return;

    var buySelector = bar.getAttribute('data-buy-box-selector') || '[data-customsirge-buy-box]';
    var buyBox = document.querySelector(buySelector);
    if (!buyBox) return;

    var heroForm = document.querySelector('form#add-to-cart');
    var heroVariants = heroForm && heroForm.querySelector('#product-variants');
    var mount = document.getElementById('customsirge-sticky-variant-mount');

    // ---- Submit proxy: add-to-cart uses the hero's current selection ----
    // Resolve the hero button ON EVERY CLICK, never cache it: productScript's removeDisabledAttribute()
    // replaces the add-to-cart button with a clone ("to remove all event listeners") on every variant
    // update, so a reference captured at init points at a detached node and .click() silently does
    // nothing. Falls back to requestSubmit()/submit() if the button cannot be found at all.
    var submit = bar.querySelector('[data-customsirge-sticky-submit]');
    if (submit && heroForm) {
      submit.addEventListener('click', function () {
        var btn = heroForm.querySelector('#product-add-to-cart, button[name="add"]');
        if (btn) {
          btn.click();
        } else if (typeof heroForm.requestSubmit === 'function') {
          heroForm.requestSubmit();
        } else {
          heroForm.submit();
        }
      });
    }

    // ---- Show/hide on scroll (unchanged); also lazily runs the variant pipeline on first show ----
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          var show = entry && !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
          bar.classList.toggle('customsirge-sticky-atc--visible', show);
          document.documentElement.classList.toggle('customsirge-sticky-atc-active', show);
          if (show) { bar.removeAttribute('hidden'); setup(); } else { bar.setAttribute('hidden', ''); }
        },
        { root: null, threshold: 0, rootMargin: '0px 0px -40% 0px' }
      );
      observer.observe(buyBox);
    } else {
      window.addEventListener('scroll', function () {
        var rect = buyBox.getBoundingClientRect();
        var show = rect.bottom < 0;
        bar.classList.toggle('customsirge-sticky-atc--visible', show);
        if (show) { bar.removeAttribute('hidden'); setup(); } else { bar.setAttribute('hidden', ''); }
      });
    }

    // ---- Variant proxy sync ----
    if (!mount || !heroVariants) return;

    var stickySizeStyled = null;
    var stickySizeSelect = null;
    var pipelineDone = false;
    var polling = false;

    // Kaching bundles mode: the hero still RENDERS the native pickers, it just hides the whole block
    // (product-hero.liquid adds `customsirge-native-pickers-hidden` / `data-customsirge-bundle-mode`;
    // customsirge_product.css sets display:none !important on it). The controls stay functional — they
    // are simply not shown — so the sticky bar must find them by DOM presence instead of by geometry.
    var bundleMode = !!(heroVariants.classList.contains('customsirge-native-pickers-hidden') ||
      (heroVariants.closest && heroVariants.closest('[data-customsirge-bundle-mode]')));

    function isVisible(el) {
      return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
    }

    // `display:none` on an ancestor zeroes offsetWidth/offsetHeight/getClientRects() for EVERY
    // descendant, so in bundle mode `.filter(isVisible)` returns nothing and heroReady() never fires.
    // Select by class there instead: productScript builds the real controls into wrappers WITHOUT
    // `single-option-selector`; the server-rendered set carrying that class is the hidden duplicate
    // (product-hero.liquid) that visibility used to filter out for us, so exclude it explicitly.
    function notRawDuplicate(el) {
      var w = el.closest && el.closest('.selector-wrapper');
      return !(w && w.classList.contains('single-option-selector'));
    }

    // In bundle mode PREFER the non-duplicate set, but never return an empty list because of that
    // preference: productScript inserts its pills into whichever .selector-wrapper it finds
    // (insertAdjacentHTML on the wrapper itself), so depending on whether Shopify's OptionSelectors
    // produced a second, unclassed set, the real controls may live in wrappers that DO carry
    // `single-option-selector`. Excluding them outright rendered an empty sticky bar. Falling back to
    // the full list is safe: buildStickyControls() dedupes pill groups by radio name.
    function preferNonRaw(list, isRaw) {
      var nonRaw = list.filter(function (el) { return !isRaw(el); });
      return nonRaw.length ? nonRaw : list;
    }

    function sourcePills() {
      var all = Array.prototype.slice.call(heroVariants.querySelectorAll('.radio-selector'));
      if (!bundleMode) return all.filter(isVisible);
      return preferNonRaw(all, function (el) { return !notRawDuplicate(el); });
    }
    function sourceSizeSelect() {
      var wraps = Array.prototype.slice.call(heroVariants.querySelectorAll('.type-size'));
      wraps = bundleMode
        ? preferNonRaw(wraps, function (w) { return w.classList.contains('single-option-selector'); })
        : wraps.filter(isVisible);
      for (var i = 0; i < wraps.length; i++) { var s = wraps[i].querySelector('select'); if (s) return s; }
      return null;
    }

    // The sticky copies live outside the hero's hidden block, so clear any hiding they inherited.
    function unhideClone(root) {
      if (!root) return;
      root.classList.remove('customsirge-native-pickers-hidden');
      if (root.style && root.style.display === 'none') root.style.display = '';
      Array.prototype.slice.call(
        root.querySelectorAll('[style*="display"], .customsirge-native-pickers-hidden')
      ).forEach(function (el) {
        el.classList.remove('customsirge-native-pickers-hidden');
        if (el.style.display === 'none') el.style.display = '';
      });
    }

    function heroReady() {
      return sourcePills().length > 0 || !!sourceSizeSelect();
    }

    function whenHeroReady(cb) {
      // The hero pills/size are built by an async, fetch-gated initProductScript; wait for them.
      if (heroReady()) { cb(); return; }
      if (polling) return;
      polling = true;
      var tries = 0;
      var t = setInterval(function () {
        tries++;
        if (heroReady()) { clearInterval(t); polling = false; cb(); }
        else if (tries > 55) { clearInterval(t); polling = false; } // ~8s cap; 1-variant products have no options
      }, 150);
    }

    function setup() {
      if (pipelineDone) return;
      whenHeroReady(function () {
        if (pipelineDone) return;
        buildStickyControls();
        wireSize();
        wirePills();
        wireHeroToSticky();
        reflect();
        pipelineDone = true;
      });
    }

    function optionNameFor(rs, groupName) {
      var w = rs.closest('.selector-wrapper');
      var lbl = w && w.querySelector('label:not(.variant-label)');
      if (lbl && lbl.textContent.trim()) return lbl.textContent.trim();
      return (groupName || '').replace(/-radio$/, '').replace(/-/g, ' ');
    }

    // Rebuild the sticky UI from ONLY the visible controls (no hidden duplicate set, no master select).
    function buildStickyControls() {
      if (mount.dataset.built) return;
      var container = document.createElement('div');
      container.id = 'customsirge-sticky-cloned-variants';

      // Profile / Arch pill groups (dedupe by radio name)
      var seen = {};
      sourcePills().forEach(function (rs) {
        var radio = rs.querySelector('input[type="radio"]');
        var name = radio && radio.name;
        if (!name || seen[name]) return;
        seen[name] = true;

        var group = document.createElement('div');
        group.className = 'selector-wrapper';
        var lbl = document.createElement('label');
        lbl.textContent = optionNameFor(rs, name);
        group.appendChild(lbl);

        var rsClone = rs.cloneNode(true);
        rsClone.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
        rsClone.querySelectorAll('label[for]').forEach(function (l) { l.removeAttribute('for'); });
        unhideClone(rsClone);
        group.appendChild(rsClone);

        // Compact <select> alternative for this group — shown on mobile via CSS, where the
        // pills would wrap to multiple rows and make the bar too tall. Native <select> gives
        // the native mobile picker and can't leave the viewport. Drives the hero like the pills.
        var sel = document.createElement('select');
        sel.className = 'customsirge-sticky-atc__optselect';
        sel.setAttribute('data-optname', name);
        sel.setAttribute('aria-label', lbl.textContent);
        Array.prototype.slice.call(rs.querySelectorAll('input[type="radio"]')).forEach(function (r) {
          var o = document.createElement('option');
          o.value = r.value;
          var optWrap = r.closest('.radio-selector__option');
          var optLbl = optWrap && (optWrap.querySelector('.variant-label') || optWrap.querySelector('label'));
          o.textContent = ((optLbl ? optLbl.textContent : r.value) || r.value).trim();
          if (r.checked) o.selected = true;
          sel.appendChild(o);
        });
        group.appendChild(sel);

        container.appendChild(group);
      });

      // Size <select> (rebuilt as a dropdown; StyledSelect wired in wireSize)
      var heroSize = sourceSizeSelect();
      if (heroSize) {
        var sizeGroup = document.createElement('div');
        sizeGroup.className = 'selector-wrapper type-size';
        var sw = heroSize.closest('.type-size');
        var sLblSrc = sw && sw.querySelector('label:not(.variant-label)');
        var sLbl = document.createElement('label');
        sLbl.textContent = (sLblSrc && sLblSrc.textContent.trim()) || 'Size';
        sizeGroup.appendChild(sLbl);

        stickySizeSelect = heroSize.cloneNode(true);
        stickySizeSelect.removeAttribute('id');
        delete stickySizeSelect.dataset.styledSelect;
        unhideClone(stickySizeSelect);
        sizeGroup.appendChild(stickySizeSelect);
        container.appendChild(sizeGroup);
      }

      mount.appendChild(container);
      mount.dataset.built = '1';
    }

    // Re-init the theme's StyledSelect on the rebuilt size <select> for parity with the hero.
    function wireSize() {
      if (!stickySizeSelect) return;
      if (window.StyledSelect) {
        try {
          delete stickySizeSelect.dataset.styledSelect;
          stickySizeStyled = new window.StyledSelect(stickySizeSelect, { placeholder: '', isSizeSelector: true, openUp: true });
          stickySizeSelect.dataset.styledSelect = 'true'; // stop styled-select.js's global observer double-initing it
        } catch (e) { stickySizeStyled = null; }
      }
      if (!stickySizeStyled) { stickySizeSelect.style.display = ''; } // native <select> fallback
      forceDropdownUp();
      // Some builds re-create the list on open, so re-assert the direction on every button click.
      mount.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('.styled-select-button')) setTimeout(forceDropdownUp, 0);
      });
      // Wire AFTER construction so StyledSelect's own init change doesn't drive the hero.
      stickySizeSelect.addEventListener('change', function () { driveHeroSize(stickySizeSelect.value); });
    }

    // The sticky bar is pinned to the BOTTOM of the viewport, so its size list must open upward or it
    // spills off-screen. StyledSelect's `openUp` option only exists in the SOURCE assets/styled-select.js,
    // which is never loaded on a PDP — the page gets the copies embedded in product-bundle.min.js /
    // interactive-bundle.min.js, and those ignore the option entirely. So re-point the list here instead.
    // This only depends on the `.styled-select-dropdown` class name, which every build shares, and it
    // no-ops if a build ever does honour `openUp`.
    function forceDropdownUp() {
      var list = mount.querySelector('.type-size .styled-select-dropdown');
      if (!list || list.style.bottom === '100%') return;
      list.style.top = 'auto';
      list.style.bottom = '100%';
      list.style.borderTop = '1px solid #d1d5db';
      list.style.borderBottom = 'none';
      list.style.borderRadius = '0.5rem 0.5rem 0 0';
      list.style.boxShadow = '0 -4px 6px -1px rgba(0, 0, 0, 0.1)';
    }

    function driveHeroSize(v) {
      var hs = sourceSizeSelect();
      if (hs && hs.value !== v) { hs.value = v; hs.dispatchEvent(new Event('change', { bubbles: true })); }
    }

    // Find the hero radio for an option value; prefer the productScript-wired control — identified by
    // visibility normally, and by "not the hidden duplicate set" in bundle mode (where nothing is
    // visible, so the plain fallback could otherwise return a radio from the duplicate wrappers).
    function heroRadioFor(name, value) {
      var match = null, preferred = null;
      heroVariants.querySelectorAll('input[type="radio"]').forEach(function (r) {
        if (r.name !== name || r.value !== value) return;
        if (!match) match = r;
        var rs = r.closest('.radio-selector');
        if (!rs || preferred) return;
        if (bundleMode ? notRawDuplicate(r) : isVisible(rs)) preferred = r;
      });
      return preferred || match;
    }

    // Sticky pill click -> drive the matching hero radio.
    function wirePills() {
      if (mount.dataset.wired) return;
      mount.dataset.wired = '1';
      mount.addEventListener('click', function (e) {
        var opt = e.target.closest && e.target.closest('.radio-selector__option');
        if (!opt || !mount.contains(opt)) return;
        var radio = opt.querySelector('input[type="radio"]');
        if (!radio) return;
        e.preventDefault();
        var hero = heroRadioFor(radio.name, radio.value);
        if (hero && !hero.checked) hero.click();
        else reflect();
      });
      // Mobile Profile/Arch dropdowns -> drive the matching hero radio (the size <select> is
      // filtered out here — it has its own listener wired in wireSize).
      mount.addEventListener('change', function (e) {
        var sel = e.target;
        if (!sel || !sel.classList || !sel.classList.contains('customsirge-sticky-atc__optselect')) return;
        var hero = heroRadioFor(sel.getAttribute('data-optname'), sel.value);
        if (hero && !hero.checked) hero.click();
        else reflect();
      });
    }

    // Hero change -> reflect authoritative state into the sticky.
    function wireHeroToSticky() {
      if (heroVariants.dataset.stickyBound) return;
      heroVariants.dataset.stickyBound = '1';
      heroVariants.addEventListener('change', reflect);
    }

    function reflect() {
      var container = mount.querySelector('#customsirge-sticky-cloned-variants');
      if (!container) return;
      var stickyRadios = Array.prototype.slice.call(container.querySelectorAll('input[type="radio"]'));

      // Pills: mirror each hero group's checked value + checked styling (matches productScript's updateCheckedStyles).
      var handled = {};
      var optSelects = Array.prototype.slice.call(container.querySelectorAll('.customsirge-sticky-atc__optselect'));
      sourcePills().forEach(function (rs) {
        var chk = rs.querySelector('input[type="radio"]:checked');
        if (!chk || handled[chk.name]) return;
        handled[chk.name] = true;
        stickyRadios.forEach(function (r) {
          if (r.name !== chk.name) return;
          r.checked = (r.value === chk.value);
          var label = r.nextElementSibling;
          if (label && label.classList && label.classList.contains('variant-label')) {
            label.style.borderColor = r.checked ? '#00aeef' : '#D9D9D9';
            label.style.boxShadow = r.checked ? '0px 8.5px 26px 0px rgba(0,0,0,0.15)' : 'none';
          }
        });
        // Mobile dropdown for this group mirrors the checked value (no dispatch → no loop).
        optSelects.forEach(function (sel) {
          if (sel.getAttribute('data-optname') === chk.name && sel.value !== chk.value) sel.value = chk.value;
        });
      });

      // Size: mirror value without dispatching (avoids a feedback loop).
      var hs = sourceSizeSelect();
      if (hs && stickySizeSelect) {
        if (stickySizeStyled && typeof stickySizeStyled.setValue === 'function') {
          stickySizeStyled.setValue(hs.value);
        } else if (stickySizeSelect.value !== hs.value) {
          stickySizeSelect.value = hs.value;
        }
      }

      updateStickyPrice();
    }

    function currentVariant() {
      var idInput = heroForm.querySelector('[name="id"]');
      var vid = idInput && idInput.value;
      var variants = (window.product && window.product.variants) || [];
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(vid)) return variants[i];
      }
      return null;
    }

    function updateStickyPrice() {
      if (typeof window.formatMoney !== 'function') return;
      var v = currentVariant();
      if (!v) return;
      var priceEl = bar.querySelector('[data-sticky-price]');
      if (priceEl) priceEl.innerHTML = window.formatMoney(v.price, window.money_format);

      var cont = bar.querySelector('.customsirge-sticky-atc__price');
      var cmp = bar.querySelector('[data-sticky-compare]');
      var onSale = v.compare_at_price && v.compare_at_price > v.price;
      if (onSale) {
        if (!cmp && cont) {
          cmp = document.createElement('s');
          cmp.setAttribute('data-compare', '');
          cmp.setAttribute('data-sticky-compare', '');
          cont.appendChild(cmp);
        }
        if (cmp) { cmp.innerHTML = window.formatMoney(v.compare_at_price, window.money_format); cmp.style.display = ''; }
      } else if (cmp) {
        cmp.style.display = 'none';
      }
    }

    // Kick off now; also runs lazily on first show (both idempotent).
    setup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
